// frontend/components/settings/SettingsModal.tsx

"use client";

import { useState } from "react";
import { useSession } from "next-auth/react";
import { useTheme } from "next-themes";
import { X } from "lucide-react";
import ProfileEditSection from "./ProfileEditSection";
import GeneralSettingsTab from "./GeneralSettingsTab";
import AdminMemberTab from "./AdminMemberTab";
import AdminSlackTab from "./AdminSlackTab";
import AdminAuditLogTab from "./AdminAuditLogTab";

type NavItem = "profile" | "general" | "members" | "slack" | "audit";

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  colorTheme: string;
  onColorChange: (color: string) => void;
}

export default function SettingsModal({ isOpen, onClose, colorTheme, onColorChange }: SettingsModalProps) {
  const { theme, setTheme } = useTheme();
  const { data: session } = useSession();
  const [active, setActive] = useState<NavItem>("profile");

  const handleThemeChange = (newMode: string, newColor: string) => {
    setTheme(newMode);
    onColorChange(newColor);
    const userId = (session?.user as any)?.id;
    if (userId) {
      localStorage.setItem(`alpha-color-theme-${userId}`, newColor);
      localStorage.setItem(`alpha-theme-mode-${userId}`, newMode);
    }
  };

  if (!isOpen) return null;

  const navItems: { key: NavItem; label: string; adminOnly?: boolean }[] = [
    { key: "profile", label: "프로필 수정" },
    { key: "general", label: "일반 설정" },
    { key: "members", label: "멤버 관리", adminOnly: true },
    { key: "slack",   label: "알림 설정",  adminOnly: true },
    { key: "audit",   label: "감사 로그",  adminOnly: true },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white dark:bg-zinc-900 w-195 max-w-[95vw] rounded-xl shadow-2xl flex overflow-hidden border border-gray-200 dark:border-zinc-800 max-h-[85vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* 좌: 네비게이션 */}
        <div className="w-44 shrink-0 border-r border-gray-100 dark:border-zinc-800 flex flex-col py-4">
          <div className="px-4 pb-3 border-b border-gray-100 dark:border-zinc-800 mb-2 flex items-center justify-between">
            <span className="text-sm font-bold text-gray-800 dark:text-zinc-100">설정 및 관리</span>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded text-gray-400">
              <X size={15} />
            </button>
          </div>
          <nav className="flex flex-col gap-0.5 px-2">
            {navItems
              .filter(item => !item.adminOnly || session?.user?.role === "ADMIN")
              .map(item => (
                <button
                  key={item.key}
                  onClick={() => setActive(item.key)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    active === item.key
                      ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                      : "text-gray-600 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 hover:text-gray-800 dark:hover:text-zinc-100"
                  }`}
                >
                  {item.label}
                </button>
              ))}
          </nav>
        </div>

        {/* 우: 콘텐츠 */}
        <div className="flex-1 overflow-y-auto px-6 py-5 text-gray-700 dark:text-zinc-300">
          {active === "profile" && <ProfileEditSection session={session} />}
          {active === "general" && (
            <GeneralSettingsTab
              theme={theme}
              colorTheme={colorTheme}
              handleThemeChange={handleThemeChange}
              session={session}
              accessToken={(session?.user as any)?.accessToken}
            />
          )}
          {active === "members" && <AdminMemberTab session={session} />}
          {active === "slack"   && <AdminSlackTab accessToken={(session?.user as any)?.accessToken} />}
          {active === "audit"   && <AdminAuditLogTab accessToken={(session?.user as any)?.accessToken} />}
        </div>
      </div>
    </div>
  );
}
